/**
 * Highlights {{ … }} expressions inside an input/textarea: the real input
 * renders its text transparent (caret stays visible) while this overlay,
 * portalled into the same InputBase root, paints the identical text with the
 * expressions as blue pills. Box and scroll position are mirrored to stay in
 * lockstep.
 */

import { CSSProperties, FC, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { xxllncColor } from '../../../layout/theme/color'
import { tokenizeExpressions } from './expressionTokens'

/** Matches MUI's default body text so plain segments look unchanged. */
export const INPUT_TEXT_COLOR = 'rgba(0, 0, 0, 0.87)'

interface InputBox {
  top: number
  left: number
  width: number
  height: number
  padding: string
}

const expressionSx: CSSProperties = {
  color: xxllncColor.opBlue80,
  background: xxllncColor.opBlue10,
  borderRadius: 4,
}

const ExpressionHighlightOverlay: FC<{
  inputEl: HTMLInputElement | HTMLTextAreaElement
  value: string
}> = ({ inputEl, value }) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [box, setBox] = useState<InputBox | null>(null)

  useLayoutEffect(() => {
    const update = () => {
      const cs = window.getComputedStyle(inputEl)
      setBox({
        top: inputEl.offsetTop,
        left: inputEl.offsetLeft,
        width: inputEl.clientWidth,
        height: inputEl.clientHeight,
        padding: `${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`,
      })
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(inputEl)

    const syncScroll = () => {
      if (contentRef.current) {
        contentRef.current.style.transform =
          `translate(${-inputEl.scrollLeft}px, ${-inputEl.scrollTop}px)`
      }
    }
    inputEl.addEventListener('scroll', syncScroll)
    syncScroll()
    return () => {
      observer.disconnect()
      inputEl.removeEventListener('scroll', syncScroll)
    }
  }, [inputEl])

  // Re-mirror the scroll position after every render: typing can scroll the
  // input without firing a scroll event before the next paint.
  useLayoutEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.transform =
        `translate(${-inputEl.scrollLeft}px, ${-inputEl.scrollTop}px)`
    }
  })

  const root = inputEl.parentElement
  if (!root || !box) return null

  const isTextarea = inputEl.tagName === 'TEXTAREA'

  return createPortal(
    <div
      aria-hidden
      style={{
        position: 'absolute',
        top: box.top,
        left: box.left,
        width: box.width,
        height: box.height,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <div
        ref={contentRef}
        style={{
          padding: box.padding,
          whiteSpace: isTextarea ? 'pre-wrap' : 'pre',
          overflowWrap: isTextarea ? 'break-word' : undefined,
          color: INPUT_TEXT_COLOR,
        }}
      >
        {tokenizeExpressions(value).map((token, i) =>
          token.isExpression ? (
            <span key={`${i}-${token.text}`} style={expressionSx}>{token.text}</span>
          ) : (
            <span key={`${i}-${token.text}`}>{token.text}</span>
          ),
        )}
      </div>
    </div>,
    root,
  )
}

export default ExpressionHighlightOverlay
